import { cloneDeep } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { TacticCardVeevaStatusStyle, TacticChannelGroupStyle } from './TacticCard.styles';
import { errorToast, succesToast } from 'basics/utils/toast';
import { Tactic } from 'generated/maya.types';
import { Channel, TacticMode, TacticTabsType, VeevaSyncStatus } from 'modules/maya/basics/enums/maya.enums';
import { CepStateType } from 'modules/maya/basics/types/maya.types';
import { useSetTacticStatusToDeletedMutation } from 'modules/maya/graphql/mutations/setTacticStatusToDeleted';
import cepState from 'modules/maya/states/cep.states';
import tacticState from 'modules/maya/states/tactic.states';
import { YodaColors } from 'yoda-ui/yodaTheme';

const useTacticCard = (tactic: Tactic) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const [setTacticStatusToDeleted, { loading: removeTacticLoading }] = useSetTacticStatusToDeletedMutation();

  const isChannelMatch = (channel: Channel) => {
    return Object.keys(Channel).find((key) => Channel[key as keyof typeof Channel] === channel);
  };

  const getCategoryForChannel = (channel: Channel) => {
    switch (channel) {
      case isChannelMatch(Channel.approvedEmail):
      case isChannelMatch(Channel.broadcastEmail):
        return TacticTabsType.EMAIL;
      case isChannelMatch(Channel.virtualEvent):
      case isChannelMatch(Channel.f2fEvent):
      case isChannelMatch(Channel.hybridEvent):
        return TacticTabsType.EVENT;
      case isChannelMatch(Channel.f2fMeeting):
      case isChannelMatch(Channel.oneToOne):
      case isChannelMatch(Channel.virtualMeeting):
      case isChannelMatch(Channel.telephone):
        return TacticTabsType.INTERACTION;
      case isChannelMatch(Channel.webLogIn):
      case isChannelMatch(Channel.webNoLogIn):
        return TacticTabsType.WEB;
      default:
        return null;
    }
  };

  const getStyleFromCategory = (group: TacticTabsType | null, channel: keyof typeof Channel) => {
    switch (group) {
      case TacticTabsType.EMAIL:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: channel === 'approvedEmail'
            ? YodaColors.greenSuccessDark
            : YodaColors.green2,
        };
      case TacticTabsType.EVENT:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: YodaColors.yellow,
        };
      case TacticTabsType.INTERACTION:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: YodaColors.purple,
        };
      case TacticTabsType.WEB:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: YodaColors.primaryBlue,
        };
      default:
        return null;
    }
  };

  const getStyleFromVeevaSyncStatus = (status: VeevaSyncStatus) => {
    switch (status) {
      case VeevaSyncStatus.ERROR:
        return {
          ...TacticCardVeevaStatusStyle,
          border: `solid ${YodaColors.errorLight}`,
          color: YodaColors.error,
          backgroundColor: YodaColors.errorLight,
        };
      case VeevaSyncStatus.PENDING:
        return {
          ...TacticCardVeevaStatusStyle,
          border: `solid ${YodaColors.warningLight}`,
          color: YodaColors.warning,
          backgroundColor: YodaColors.warningLight,
        };
      case VeevaSyncStatus.SYNC:
        return {
          ...TacticCardVeevaStatusStyle,
          border: `solid ${YodaColors.greenSuccessLight}`,
          color: YodaColors.greenSuccessDark,
          backgroundColor: YodaColors.greenSuccessLight,
        };
      default:
        return null;
    }
  };

  const editTacticCallback = () => {
    tacticState.tactic = tactic;
    tacticState.mode = TacticMode.form;
  };

  const deleteTacticCallback = async (tacticId: string) => {
    const isTacticDeleted = await setTacticStatusToDeleted(tacticId);

    if (isTacticDeleted.data) {
      succesToast(t('maya_action_deleting_tactic_success'));
      const clonedTactics = cloneDeep(cepStateValue.tactics);
      const targetTacticsIndex = clonedTactics.findIndex((element) => { return element._id === tacticId; });
      if (targetTacticsIndex !== -1) {
        clonedTactics.splice(targetTacticsIndex, 1);
        cepState.tactics = clonedTactics;
      }
    } else if (isTacticDeleted.error) {
      errorToast(isTacticDeleted.error);
    }
    setOpen(false);
  };

  const openModalCallback = (tacticId: string) => {
    setOpen(true);
    if (tacticState.tactic) {
      tacticState.tactic._id = tacticId;
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      succesToast(t('maya_successfull_copying', { text }));
    } catch (error) {
      errorToast(error as string);
    }
  };

  return {
    deleteTacticCallback,
    editTacticCallback,
    getCategoryForChannel,
    getStyleFromCategory,
    getStyleFromVeevaSyncStatus,
    handleCopy,
    open,
    openModalCallback,
    removeTacticLoading,
    setOpen,
  };
};

export default useTacticCard;
